<template>
    <div class="page__content settings">
        <div class="columns">
            <div class="column is-12 is-8-desktop">
                <h2 class="page__headline">{{ $t('settings.intro.headline') }}</h2>
                <p class="page__copy">
                    {{ $t('settings.intro.copy') }}
                </p>
            </div>
        </div>
      <form @submit.prevent novalidate>
        <div class="edit-box">
                <div class="edit-box__section">
                    <CampgroundSettings
                        ref="settings"
                        :value.sync="settings"
                        :currencies.sync="currencies"
                        :languages.sync="languages"
                        :form-errors="formErrors"
                        :currency-change-enabled="currencyChangeEnabled"
                    />
                </div>

                <div class="edit-box__footer is-clearfix">
                    <p class="control">
                        <button
                            class="button is-primary is-pulled-right"
                            :class="{ 'is-loading' : saving }"
                            @click="save"
                        >{{
                            $t('generic.save') }}
                        </button>
                    </p>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
    import Slider from 'vue-bulma-slider';
    import Multiselect from 'vue-multiselect';
    import {Api} from '../../../api';
    import {AppModel} from '../../../models';
    import {EventBus, Tracking} from '../../../utils';
    import {Wysiwyg, FormInput, PublishSwitch, PaymentMethod, CampgroundSettings} from '../../components';
    import Attributes from '../../components/attributes';

    const MIN_DEPOSIT = 20;

    export default {
        name: 'settings',

        MIN_DEPOSIT,

        components: {
            Attributes,
            Wysiwyg,
            FormInput,
            Slider,
            PublishSwitch,
            Multiselect,
            PaymentMethod,
            CampgroundSettings,
        },

        data() {
            return {
                depositInfo: AppModel.depositInfo,
                showAttributesModal: false,
                allAttributes: [],
                attributeCategories: [],
                SERVICE_EMAIL_LINK: AppModel.serviceEmailLink,
                saving: false,
                country: 'de',
                formErrors: {},
                showSuccess: false,
                hasErrors: false,
                showCurrencyModal: false,
                paymentDataType: 'card',
                paymentData: {},
                creditCardYearPlaceholder: 2030,
                wysiwygOptions: {
                    maxChars: 600,
                    placeholder: this.$t('settings.additionalRules.placeholder'),
                },
                formatting: {
                    iban: {
                        blocks: [4, 4, 4, 4, 4, 2],
                        uppercase: true,
                    },
                },
                settings: {
                    email: '',
                    bankaccount_owner: '',
                    bankaccount_iban: '',
                    payment: {
                        card: {},
                        bank: {},
                    },
                    deposit_percent: AppModel.depositInfo.default_percent,
                    minimum_fixed_deposit: '00.00',
                    accommodation_minimum_fixed_deposit: '0.00',
                    accommodation_deposit_percent: AppModel.depositInfo.default_percent,
                    arrival_from: null,
                    departure_until: null,
                    accommodation_arrival_from: null,
                    accommodation_departure_until: null,
                    additional_rules: '',
                    toc_url: '',
                    privacy_policy_url: '',
                    languages: [],
                    currency: null,
                    payment_system_pitches: null,
                    payment_system_rentals: null,
                    payment_provider: null,
                    payment_provider_config: null,
                    checkout_config: {
                        input_guests_nationality: 'n'
                    },
                    owner_frontend_config: {
                        pms_objects: true
                    }
                },
                languages: [],
                currencies: [],
                settingsBeforeChangesApply: null,
                updateDepositTrackingTimeout: null,
                currencyChangeEnabled: false,
                campground: AppModel.activeCampground,
                node_env: process.env.NODE_ENV,
            };
        },

        async created() {
            EventBus.$on(EventBus.UPDATE_SETTINGS_DONE, this.onSettingsSaved);
            this.languages = AppModel.languages;
            this.currencies = AppModel.currencies;
            this.country = AppModel.activeCampground.country;

            for(let c of AppModel.validBankCountries) {
                if(c === AppModel.activeCampground.country) {
                    this.paymentDataType = 'bank';
                }
            }

            this.setPaymentData();
            this.creditCardYearPlaceholder = new Date().getFullYear() + 2;
            this.currencyChangeEnabled = !!process.env.CAN_CHANGE_CURRENCY;
        },

        async beforeRouteEnter(to, from, next) {
            await Api.bootstrap();

            const settings = await Api.settings.fetchSettings();

            next((vm) => {
                vm.settings = settings.body;
            });
        },

        methods: {
            save() {
                this.saving = true;
                this.formErrors = {};
                this.showSuccess = false;
                this.hasErrors = false;
                this.$set(this.settings, 'currency', this.$refs.settings.selectedCurrency);
                this.$set(this.settings, 'languages', this.$refs.settings.selectedLanguages);

                if(this.settings.card_number) {
                    this.settings.card_number = this.settings.card_number.replace(/\s/g, '');
                }
                console.log('settings', this.settings);
                Api.settings.updateSettings(this.settings)
                    .then((response) => {
                        if(!response.ok) {
                            this.onError(response.body);
                        } else if(this.settingsBeforeChangesApply) {
                            // this works because every currency change triggers a reload
                            const hasCurrencyChanged = this.settingsBeforeChangesApply.currency !== this.$refs.settings.selectedCurrency;
                            if(hasCurrencyChanged) {
                                window.location.reload();
                            }
                        }
                    }, (response) => {
                        console.log('updateSettings failed', response);
                        this.onError(response.body);
                    });
            },

            onSettingsSaved(data) {
                this.saving = false;
                this.showSuccess = true;
                this.hasErrors = false;
                EventBus.$emit(EventBus.UPDATE_CHECKS);
                EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`, direction: 'Up', body: this.$t('generic.saveSuccess') });
                Tracking.trackEvent('Settings', 'changeSettings', 'success', null);
            },

            onError(errors) {
                this.formErrors = errors;
                this.hasErrors = true;
                this.showSuccess = false;
                EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('generic.formErrorHeadline')}</h3>`, direction: 'Up', type: 'danger', body: this.$t('generic.formError') });
                Tracking.trackEvent('Settings', 'changeSettings', 'failed', null);
            },
            setPaymentData() {
                if(!this.settings.payment) {
                    this.settings.payment = {};
                }
                if(!this.settings.payment.bank) {
                    this.settings.payment.bank = {};
                }
                if(!this.settings.payment.card) {
                    this.settings.payment.card = {};
                }
                if(this.paymentDataType === 'bank') {
                    this.paymentData = this.settings.payment.bank;
                } else {
                    this.paymentData = this.settings.payment.card;
                }
            },
        },
    };
</script>

<style lang="scss">
    @import 'settings.scss';

    .settings {
        &__input {
            display: inline-block;
            width: 75%;
        }

        &__input--file-upload {
            display: inline-flex;
            width: 20%;
            height: 32px;
            padding: 0 !important;
            justify-content: center;
        }

        &__input--is-hidden {
            display: none;
        }
    }
</style>

<style lang="scss" scoped>
.align-vertical {
    display: inline-flex;
    height: 100%;
    align-items: center;
}
</style>



// WEBPACK FOOTER //
// src/views/pages/settings/settings.vue